import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { clearSession, readSession, saveSession, sessionEventName } from './storage';
import type { AuthSession } from './types';
import { AuthContext } from './context';
import { getNextExpiryDelay } from './expiry';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => readSession());
  const queryClient = useQueryClient();
  const sessionToken = useRef(session?.accessToken);

  useEffect(() => {
    const synchronize = (): void => {
      const next = readSession();
      if (next?.accessToken !== sessionToken.current) {
        // KYC observations, selfie codes and wallet data must not survive an
        // account switch, including one received through another browser tab.
        queryClient.clear();
        sessionToken.current = next?.accessToken;
      }
      setSession(next);
    };
    window.addEventListener('storage', synchronize);
    window.addEventListener(sessionEventName, synchronize);
    return () => {
      window.removeEventListener('storage', synchronize);
      window.removeEventListener(sessionEventName, synchronize);
    };
  }, [queryClient]);

  const expiresAt = session?.expiresAt;

  useEffect(() => {
    if (expiresAt === undefined) return undefined;

    let timeout: number | undefined;
    const tick = (): void => {
      if (Date.now() >= expiresAt) {
        clearSession('expired');
        return;
      }
      timeout = window.setTimeout(tick, getNextExpiryDelay(expiresAt));
    };

    timeout = window.setTimeout(tick, getNextExpiryDelay(expiresAt));
    return () => {
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [expiresAt]);

  const signIn = useCallback((nextSession: AuthSession): void => saveSession(nextSession), []);
  const signOut = useCallback((): void => clearSession(), []);
  const value = useMemo(() => ({ session, signIn, signOut }), [session, signIn, signOut]);

  return <AuthContext.Provider value={value}><Fragment key={session?.accessToken ?? 'signed-out'}>{children}</Fragment></AuthContext.Provider>;
}
